// TODO(stripe-reimpl): this file has legacy Stripe billing code, disabled while we're betaAccess-only. Rebuild on the new billing system.
import React, { useState, useContext, useEffect } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "context/Auth";
import { useAuth } from "hooks/useAuth";
import { useColorMode } from "context/ColorMode";
import capitalize from "../../utils/helpers/capitalize";
import {
  Alert,
  Box,
  Typography,
  Skeleton,
  Button,
  AlertTitle,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import {
  // LocalHospitalRounded, // with the "Your Facility" section below
  BusinessRounded,
  DarkModeRounded,
  LightModeRounded,
  DesktopWindowsRounded,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { SettingsRow, SettingsSection } from "./SettingsSection";
import DashboardPageHeader from "components/common/DashboardPageHeader";
// import AddressSettings from "./AddressSettings";
import AccountSettings from "./AccountSettings";
import PhoneSettings from "./PhoneSettings";
import PasswordSettings from "./PasswordSettings";
// import MarketingSettings from "./advertising/MarketingSettings";
import UsageDisplay from "./account/UsageDisplay";
import PlanManagement from "./account/PlanManagement";
import SeatManagementDialog from "./admin/SeatManagementDialog";
import DocumentProcessingSettings from "./admin/DocumentProcessingSettings";
import MfaEnrollDialog from "components/auth/MfaEnrollDialog";
import VisitStartSettingsDialog from "components/dashboard/visitStartCheckIns/VisitStartSettingsDialog";
import useMfaStatus from "hooks/useMfaStatus";
import { shouldSkipMfaEnrollmentUi } from "utils/isDevEnvironment";
import { LocationContext } from "context/Location";
import { formatDistanceToNow } from "date-fns";
import { getTrialLabelForRole } from "constants/trials";
import MorningReportBillingSettings from "features/morningReport/components/MorningReportBillingSettings";

const THEME_CHOICES = [
  {
    value: "system",
    label: "Match your device",
    icon: <DesktopWindowsRounded fontSize="small" />,
  },
  {
    value: "light",
    label: "Light",
    icon: <LightModeRounded fontSize="small" />,
  },
  { value: "dark", label: "Dark", icon: <DarkModeRounded fontSize="small" /> },
];

/**
 * Account settings. Normally opened as a dialog from the sidebar's account menu
 * (`embedded`: the dialog supplies the title). The /dashboard/settings route still
 * renders it as a page for deep links like ?tab=phone.
 */
const Settings = ({ embedded = false }) => {
  const { user, userData, subscription } = useContext(AuthContext);
  const {
    hasActiveTrial,
    hasActiveDailyPass,
    hasValidSubscription,
    hasActiveSubscription,
    subscriptionTier,
    subscriptionData,
    isBetaAdmin,
    isGlobalAdmin,
    isRegionalAdmin,
  } = useAuth();
  const { preference, setPreference } = useColorMode();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { location } = useContext(LocationContext);

  const [loading] = useState();
  const [submitted, setSubmitted] = useState();

  // const [addressOpen, setAddressOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  // const [marketingOpen, setMarketingOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [seatsOpen, setSeatsOpen] = useState(false);
  const [mfaOpen, setMfaOpen] = useState(false);
  const [checkInsOpen, setCheckInsOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const { isEnrolled, hasTOTP, hasSMS, enrolledFactors } = useMfaStatus();

  // Admin billing info
  const seatCount = subscriptionData?.items?.[0]?.quantity || 0;
  const billingInterval =
    subscriptionData?.items?.[0]?.price?.recurring?.interval === "year"
      ? "yearly"
      : "monthly";
  const seatLabel = `${seatCount} provider seat${seatCount === 1 ? "" : "s"}`;

  // Handle URL parameters for auto-opening dialogs
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "phone") {
      setPhoneOpen(true);
      // Clear the parameter to avoid reopening on refresh
      searchParams.delete("tab");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handlePortalAction = async (flowType) => {
    setActionLoading(flowType);
    try {
      const createPortalSession = firebase
        .functions()
        .httpsCallable("createPortalSession");

      const { data } = await createPortalSession({
        successUrl: window.location.href,
        cancelUrl: window.location.href,
        role: userData?.role,
        stripeId: userData?.stripeId,
        flowType,
        subscriptionId: subscriptionData?.id,
      });

      if (data.url) {
        window.location.assign(data.url);
      }
    } catch (error) {
      console.error("Error opening portal:", error);
      alert("Failed to open subscription management. Please try again.");
      setActionLoading(null);
    }
  };

  const trialLabel = getTrialLabelForRole(userData?.role);
  const isFacility = userData?.role === "facility";
  const isAdmin = userData?.role === "admin";

  const showsConsumerBilling =
    !isFacility &&
    !isAdmin &&
    !userData?.betaAccess &&
    !isGlobalAdmin &&
    !isRegionalAdmin;

  const facilityTitle =
    userData?.role === "facility" && location?.title
      ? capitalize(location.title)
      : null;

  return (
    <Box>
      {/* In the dialog the title comes from DialogTitle, and the Email row already
          says who is signed in, so only a clinic's account name is worth adding. */}
      {embedded ? (
        facilityTitle && (
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Signed in to the <strong>{facilityTitle}</strong> facility account.
          </Typography>
        )
      ) : (
        <DashboardPageHeader
          title="Settings"
          subtitle={
            <Typography>
              You're logged in as {user?.email || userData?.phone}
              {facilityTitle ? (
                <>
                  {" "}
                  using the <strong>{facilityTitle}</strong> facility account.
                </>
              ) : (
                <>.</>
              )}
            </Typography>
          }
        />
      )}

      {submitted ? (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSubmitted(null)}
        >
          {submitted}
        </Alert>
      ) : null}

      {/* Daily Pass Status Display */}
      {hasActiveDailyPass && userData?.dailyPassExpiresAt && (
        <Alert severity="info" sx={{ pt: 1, mb: 3 }}>
          <Box>
            <AlertTitle>Daily Pass Active</AlertTitle>
            Your {userData?.subscriptionTier || "Medium"} Daily Pass expires{" "}
            {formatDistanceToNow(new Date(userData.dailyPassExpiresAt), {
              addSuffix: true,
            })}
            .
          </Box>
        </Alert>
      )}

      {/* Trial Status Display */}
      {hasActiveTrial && (
        <Alert severity="success" sx={{ pt: 1, mb: 3 }}>
          <Box>
            <AlertTitle>{trialLabel} Active.</AlertTitle>
            {isFacility
              ? "Enjoying CareMap Plus? Upgrade now to keep your waiting room operations connected after the trial."
              : "Enjoying Medical SuperIntelligence? Upgrade now to continue unlimited access after your trial ends."}
            <Button
              color="inherit"
              variant="outlined"
              onClick={() => navigate("/dashboard/upgrade")}
              sx={{ mt: 2, mb: 1, display: "block" }}
            >
              Upgrade
            </Button>
          </Box>
        </Alert>
      )}

      <SettingsSection title="Appearance">
        <SettingsRow
          label="Theme"
          action={
            <ToggleButtonGroup
              exclusive
              size="small"
              value={preference}
              onChange={(e, next) => next && setPreference(next)}
              aria-label="Theme"
            >
              {THEME_CHOICES.map(({ value, label, icon }) => (
                <Tooltip key={value} title={label}>
                  <ToggleButton value={value} aria-label={label}>
                    {icon}
                  </ToggleButton>
                </Tooltip>
              ))}
            </ToggleButtonGroup>
          }
        />
      </SettingsSection>

      <SettingsSection title="Account">
        <SettingsRow
          label="Email"
          detail={user?.email || "No email added"}
          action={
            <Button
              variant="outlined"
              size="small"
              onClick={() => setAccountOpen(true)}
            >
              Change
            </Button>
          }
        />
        <SettingsRow
          label="Password"
          action={
            <Button
              variant="outlined"
              size="small"
              onClick={() => setPasswordOpen(true)}
            >
              Change
            </Button>
          }
        />
      </SettingsSection>

      {/* MFA enrollment UI omitted in dev/sandbox (sign-in MFA challenge unchanged) */}
      {!shouldSkipMfaEnrollmentUi() && (
        <SettingsSection title="Security">
          <SettingsRow
            label="Two-factor authentication"
            detail={
              isEnrolled
                ? `On${hasTOTP ? ", using an authenticator app" : hasSMS ? ", using text messages" : ""}`
                : "Protect your account and health records with a second step at sign-in"
            }
            action={
              <Button
                variant="outlined"
                size="small"
                onClick={() => setMfaOpen(true)}
              >
                {isEnrolled ? "Manage" : "Set up"}
              </Button>
            }
          />
        </SettingsSection>
      )}

      {/* Facility Info */}
      {userData?.role === "facility" && (
        <>
          {/* TODO: "Your Facility" hidden while the facility role is held
              back. It was the only way into AddressSettings and
              MarketingSettings, so both dialogs are commented out further down
              with it, along with the LocalHospitalRounded import and the
              open-state for both.

              Patient Check-ins below stays: it is the one facility surface
              still switched on, and a clinic needs to choose its questions.

          <InfoBox
            icon={
              <LocalHospitalRounded
                fontSize="large"
                sx={{ color: "#117aca" }}
              />
            }
            title="Your Facility"
            description="Manage your facility location, group settings for multi-location maps, and advertising profile."
            links={[
              {
                title: "Manage Facility & Location",
                onClick: () => setAddressOpen(true),
              },
              {
                title: "Update Your Advertising Profile",
                onClick: () => setMarketingOpen(true),
              },
            ]}
          />
          */}

          {/* Which questions the clinic's check-in asks. It lives here rather
              than on the Check-ins page itself because it is configuration a
              clinic touches about twice, and the page it belongs to is a working
              inbox the front desk reads all day. The page links here. */}
          <SettingsSection title="Patient check-ins">
            <SettingsRow
              label="Check-in questions"
              detail="Choose which questions patients answer before they are called, and turn check-ins on or off."
              action={
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCheckInsOpen(true)}
                >
                  Edit
                </Button>
              }
            />
          </SettingsSection>

          {/* Multi-Location Map URL Alert - Only show if group name is set */}
          {location?.group && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Patients can view only your {location.group} facilities at{" "}
              <Link
                href={`${window.location.origin}/map/${location.group
                  .toLowerCase()
                  .replace(/[^a-z0-9\s\-'&]/g, "")
                  .replace(/[\s'&]+/g, "-")
                  .replace(/-+/g, "-")
                  .replace(/^-|-$/g, "")}`}
                target="_blank"
                variant="body2"
              >
                {window.location.origin}/map/
                {location.group
                  .toLowerCase()
                  .replace(/[^a-z0-9\s\-'&]/g, "")
                  .replace(/[\s'&]+/g, "-")
                  .replace(/-+/g, "-")
                  .replace(/^-|-$/g, "")}
              </Link>
              .
            </Alert>
          )}

          {/* Plan Management for facilities - Only show for users with REAL subscription (not daily pass or trial) */}
          {hasValidSubscription && !hasActiveTrial && !hasActiveDailyPass && (
            <PlanManagement
              subscriptionTier={subscriptionTier}
              loading={loading}
            />
          )}
        </>
      )}

      {/* MorningReport billing: learners only (it's off for professionals for now). */}
      {userData?.role === "learner" && (
        <MorningReportBillingSettings />
      )}

      {/* Usage and plan are consumer billing. Clinics, org admins, beta users and
          platform admins don't have it. */}
      {showsConsumerBilling && (
        <UsageDisplay userData={userData} subscription={subscription} />
      )}

      {/* Only for a REAL subscription (not a daily pass or trial). */}
      {showsConsumerBilling &&
        hasValidSubscription &&
        !hasActiveTrial &&
        !hasActiveDailyPass && (
          <PlanManagement
            subscriptionTier={subscriptionTier}
            loading={loading}
          />
        )}

      {/* Admin Billing Management — [BETA-KIJABE-ADMIN] hidden for beta admin orgs (no Stripe subscription) */}
      {isAdmin && hasActiveSubscription && !isBetaAdmin && (
        <SettingsSection title="Billing and seats">
          {subscriptionData?.cancel_at_period_end && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Your subscription is scheduled to cancel. Please renew it below to
              keep your access.
            </Alert>
          )}
          {!subscriptionData?.cancel_at_period_end && (
            <SettingsRow
              label="Seats"
              detail={`${seatLabel} on your ${billingInterval} plan`}
              action={
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSeatsOpen(true)}
                >
                  Manage
                </Button>
              }
            />
          )}
          <SettingsRow
            label="Payment method"
            action={
              <LoadingButton
                variant="outlined"
                size="small"
                loading={actionLoading === "payment_method_update"}
                disabled={!!actionLoading}
                onClick={() => handlePortalAction("payment_method_update")}
              >
                {subscriptionData?.cancel_at_period_end ? "Renew" : "Change"}
              </LoadingButton>
            }
          />
          {!subscriptionData?.cancel_at_period_end && (
            <SettingsRow
              label="Subscription"
              detail="Cancel at the end of the billing period"
              action={
                <LoadingButton
                  variant="outlined"
                  size="small"
                  color="error"
                  loading={actionLoading === "subscription_cancel"}
                  disabled={!!actionLoading}
                  onClick={() => handlePortalAction("subscription_cancel")}
                >
                  Cancel
                </LoadingButton>
              }
            />
          )}
        </SettingsSection>
      )}

      {/* Document processing backend (org / global admins only; self-hides otherwise) */}
      <DocumentProcessingSettings />

      {/* Settings Dialogs */}
      {/* Unreachable without the "Your Facility" section above, which was the
          only thing that opened either. Their imports and open-state are
          commented out at the top of this file. Restore all four together.

      <AddressSettings
        user={user}
        data={userData}
        location={location}
        visible={addressOpen}
        close={() => setAddressOpen(false)}
        setSubmitted={setSubmitted}
      />

      <MarketingSettings
        user={user}
        data={userData}
        location={location}
        visible={marketingOpen}
        close={() => setMarketingOpen(false)}
        setSubmitted={setSubmitted}
      />
      */}

      <AccountSettings
        user={user}
        userData={userData}
        visible={accountOpen}
        close={() => setAccountOpen(false)}
        setSubmitted={setSubmitted}
      />

      <PhoneSettings
        user={user}
        userData={userData}
        visible={phoneOpen}
        close={() => setPhoneOpen(false)}
        setSubmitted={setSubmitted}
      />

      <PasswordSettings
        user={user}
        visible={passwordOpen}
        close={() => setPasswordOpen(false)}
        setSubmitted={setSubmitted}
      />

      <VisitStartSettingsDialog
        open={checkInsOpen}
        onClose={() => setCheckInsOpen(false)}
        clinicId={location?.id ? String(location.id) : null}
        clinicName={
          location?.title ? capitalize(location.title) : "your clinic"
        }
        onSaved={() => setSubmitted("Your check-in settings have been saved.")}
      />

      {/* Admin Seat Management Dialog */}
      <SeatManagementDialog
        open={seatsOpen}
        onClose={() => setSeatsOpen(false)}
      />

      {!shouldSkipMfaEnrollmentUi() && (
        <MfaEnrollDialog
          open={mfaOpen}
          source="settings"
          enrolledFactors={enrolledFactors}
          onClose={() => setMfaOpen(false)}
          onEnrolled={() => {
            setMfaOpen(false);
            setSubmitted(
              "Two-factor authentication has been enabled on your account.",
            );
          }}
        />
      )}
    </Box>
  );
};

export default Settings;
